import React, { useState, useEffect, useCallback } from 'react';
import analytics from '../services/AnalyticsService';
import './OnboardingStory.css';
import PlanWizard from './PlanWizard';
import { BADGE_TOTEM_STAGES, BadgeTotemIcon } from './BadgeTotem';

const AI_EMOJI_OPTIONS = ['💪', '🏋️', '🦾', '🥷', '🪖', '⚔️', '🎖️', '🦅', '🐺', '🦁', '🤖', '🧠', '⚡', '🔥', '🎯', '🏆', '👊', '🥊', '🦿', '🐉'];
const AI_TONE_PRESETS = [
  { id: 'encouraging', label: 'Encouraging', emoji: '🌟', description: 'Warm & supportive' },
  { id: 'drill_sergeant', label: 'Drill Sergeant', emoji: '🪖', description: 'Tough love' },
  { id: 'scientific', label: 'Scientific', emoji: '🔬', description: 'Data-driven' },
  { id: 'casual', label: 'Casual', emoji: '😎', description: 'Like a buddy' },
  { id: 'zen', label: 'Zen', emoji: '🧘', description: 'Calm & balanced' },
];

const SLIDES = [
  {
    id: 'potato',
    totemKey: 'potato',
    title: 'Your Journey Begins...',
    subtitle: 'As a Humble Potato',
    description: 'Every legend starts somewhere. Today, you are a potato — dormant, full of potential, waiting to sprout.',
    background: 'potato',
    particles: ['✨', '💫', '⭐'],
  },
  {
    id: 'evolution',
    totemKey: 'dragon',
    title: 'Evolve Through Consistency',
    subtitle: 'From Potato to Dragon',
    description: 'Each activity you complete transforms your badge. Stay consistent and watch yourself evolve through 13 stages!',
    background: 'evolution',
    animals: BADGE_TOTEM_STAGES.map(stage => stage.key),
    particles: ['🔥', '💥', '✨'],
  },
  {
    id: 'coach-avatar',
    emoji: '🤖',
    title: 'Meet Your AI Assistant',
    subtitle: 'Choose an Avatar',
    description: 'Pick a face for your personal fitness assistant!',
    background: 'temple',
    particles: ['🧠', '💬', '✨', '⚡'],
    isInteractive: true,
    inputType: 'coach-avatar',
  },
  {
    id: 'coach-style',
    emoji: '💬',
    title: 'Assistant Personality',
    subtitle: 'How Should They Talk?',
    description: 'Set the tone and intensity of your assistant\'s motivation style.',
    background: 'temple',
    particles: ['🎯', '⚡', '🔥', '✨'],
    isInteractive: true,
    inputType: 'coach-style',
  },
  {
    id: 'health',
    emoji: '🏥',
    title: 'Your Health Matters',
    subtitle: 'Help Us Help You',
    description: 'Do you have any health conditions we should know about? This helps us tailor recommendations safely.',
    background: 'temple',
    particles: ['💪', '❤️', '🩺', '✨'],
    isInteractive: true,
    inputType: 'health',
  },
  {
    id: 'diet',
    emoji: '🥗',
    title: 'Your Food Journey',
    subtitle: 'Dietary Preferences',
    description: 'Any dietary restrictions or preferences? Let us know so we can better support your nutrition goals.',
    background: 'awakening',
    particles: ['🥑', '🥦', '🍎', '🥕'],
    isInteractive: true,
    inputType: 'diet',
  },
  {
    id: 'begin',
    emoji: '🚀',
    title: 'Your Legend Awaits',
    subtitle: 'The Time is Now',
    description: 'Choose your path and begin your transformation. The potato\'s destiny is in your hands.',
    background: 'begin',
    particles: ['🌟', '✨', '💫', '⭐'],
    isFinal: true,
  },
];

const HEALTH_OPTIONS = [
  { id: 'none', label: 'None', emoji: '✅' },
  { id: 'heart', label: 'Heart condition', emoji: '❤️' },
  { id: 'diabetes', label: 'Diabetes', emoji: '💉' },
  { id: 'joint', label: 'Joint issues', emoji: '🦴' },
  { id: 'back', label: 'Back problems', emoji: '🔙' },
  { id: 'respiratory', label: 'Respiratory issues', emoji: '🫁' },
  { id: 'pregnancy', label: 'Pregnancy', emoji: '🤰' },
  { id: 'other', label: 'Other', emoji: '📝' },
];

const DIET_OPTIONS = [
  { id: 'none', label: 'No restrictions', emoji: '🍽️' },
  { id: 'vegetarian', label: 'Vegetarian', emoji: '🥬' },
  { id: 'vegan', label: 'Vegan', emoji: '🌱' },
  { id: 'gluten-free', label: 'Gluten-free', emoji: '🌾' },
  { id: 'dairy-free', label: 'Dairy-free', emoji: '🥛' },
  { id: 'keto', label: 'Keto', emoji: '🥓' },
  { id: 'halal', label: 'Halal', emoji: '☪️' },
  { id: 'kosher', label: 'Kosher', emoji: '✡️' },
  { id: 'allergies', label: 'Food allergies', emoji: '⚠️' },
  { id: 'other', label: 'Other', emoji: '📝' },
];

const OnboardingStory = ({ onComplete, onRefreshActivities, onOpenPlanEditor, openNewActivityModal, modalService, backendService }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedHealth, setSelectedHealth] = useState([]);
  const [selectedDiet, setSelectedDiet] = useState([]);
  const [otherHealthText, setOtherHealthText] = useState('');
  const [allergyText, setAllergyText] = useState('');
  const [otherDietText, setOtherDietText] = useState('');
  const [detectedTimezone, setDetectedTimezone] = useState('');
  
  // AI Coach customization state
  const [aiCoachEmoji, setAiCoachEmoji] = useState('🤖');
  const [aiCoachTone, setAiCoachTone] = useState('encouraging');
  const [aiCoachIntensity, setAiCoachIntensity] = useState('balanced');
  const [onboardingStartTime] = useState(Date.now());

  // Detect timezone on mount
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDetectedTimezone(tz);
    } catch (e) {
      console.warn('Could not detect timezone:', e);
    }
  }, []);
  const [showContent, setShowContent] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [showParticles, setShowParticles] = useState(false);
  const [animatedAnimals, setAnimatedAnimals] = useState([]);

  const slide = SLIDES[currentSlide];

  // Save profile data collected during onboarding
  const saveProfileData = useCallback(async () => {
    // Track health info submission
    if (selectedHealth.length > 0 || otherHealthText) {
      const conditions = [...selectedHealth];
      if (otherHealthText) conditions.push(otherHealthText);
      analytics.onboarding.healthInfoSubmitted(conditions);
    }
    
    // Track diet info submission
    if (selectedDiet.length > 0 || allergyText || otherDietText) {
      const preferences = [...selectedDiet];
      if (allergyText) preferences.push(`allergy: ${allergyText}`);
      if (otherDietText) preferences.push(otherDietText);
      analytics.onboarding.dietInfoSubmitted(preferences);
    }

    if (!backendService) return;
    
    try {
      // Build medical issues string
      let medicalIssues = selectedHealth
        .filter(h => h !== 'none' && h !== 'other')
        .map(h => HEALTH_OPTIONS.find(opt => opt.id === h)?.label)
        .filter(Boolean)
        .join(', ');
      
      if (selectedHealth.includes('other') && otherHealthText.trim()) {
        medicalIssues = medicalIssues 
          ? `${medicalIssues}, ${otherHealthText.trim()}`
          : otherHealthText.trim();
      }
      
      // Build dietary restrictions string
      let dietaryRestrictions = selectedDiet
        .filter(d => d !== 'none' && d !== 'allergies' && d !== 'other')
        .map(d => DIET_OPTIONS.find(opt => opt.id === d)?.label)
        .filter(Boolean)
        .join(', ');
      
      // Add allergy text if selected
      if (selectedDiet.includes('allergies') && allergyText.trim()) {
        const allergyEntry = `Allergies: ${allergyText.trim()}`;
        dietaryRestrictions = dietaryRestrictions 
          ? `${dietaryRestrictions}, ${allergyEntry}`
          : allergyEntry;
      }
      
      // Add other diet text if selected
      if (selectedDiet.includes('other') && otherDietText.trim()) {
        dietaryRestrictions = dietaryRestrictions 
          ? `${dietaryRestrictions}, ${otherDietText.trim()}`
          : otherDietText.trim();
      }
      
      const profileData = {};
      
      if (medicalIssues) {
        profileData.medical_issues = medicalIssues;
      }
      if (dietaryRestrictions) {
        profileData.dietary_restrictions = dietaryRestrictions;
      }
      if (detectedTimezone) {
        profileData.timezone = detectedTimezone;
      }
      
      // Save AI Coach settings
      profileData.ai_coach_settings = {
        emoji: aiCoachEmoji,
        tone_preset: aiCoachTone,
        intensity: aiCoachIntensity
      };
      
      if (Object.keys(profileData).length > 0) {
        await backendService.updateUserProfile(profileData);
      }
    } catch (error) {
      console.error('Failed to save onboarding profile data:', error);
    }
  }, [backendService, selectedHealth, selectedDiet, otherHealthText, allergyText, otherDietText, detectedTimezone, aiCoachEmoji, aiCoachTone, aiCoachIntensity]);

  const openPlanWizard = useCallback(async () => {
    await saveProfileData();
    
    // Track onboarding completed
    const duration = Date.now() - onboardingStartTime;
    analytics.onboarding.completed(duration);
    onComplete?.();
    if (modalService) {
      modalService.open(PlanWizard, {
        modalService,
        backendService,
        isOnboarding: true,
        onPlanCreated: () => {
          // Plan was created, refresh activities to show new plan items
          if (onRefreshActivities) {
            onRefreshActivities();
          }
        }
      });
    } else if (onOpenPlanEditor) {
      onOpenPlanEditor({});
    }
  }, [onComplete, onRefreshActivities, modalService, backendService, onOpenPlanEditor, saveProfileData, onboardingStartTime]);

  // Pre-generate stable particle data for all slides (so random values don't change on re-render)
  const [particleData] = useState(() => {
    return SLIDES.map((s) =>
      s.particles.map((particle, index) => ({
        particle,
        delay: index * 1.2 + (index * 0.3),
        xStart: 10 + index * 20,
        duration: 6 + index * 0.5,
      }))
    );
  });

  // Typing effect for description
  useEffect(() => {
    setTypedText('');
    setShowContent(false);
    setShowParticles(false);
    setAnimatedAnimals([]);

    const contentTimer = setTimeout(() => {
      setShowContent(true);
    }, 300);

    const particleTimer = setTimeout(() => {
      setShowParticles(true);
    }, 800);

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(particleTimer);
    };
  }, [currentSlide]);

  // Typing animation
  useEffect(() => {
    if (!showContent) return;

    const text = slide.description;
    let index = 0;

    const typingInterval = setInterval(() => {
      if (index <= text.length) {
        setTypedText(text.slice(0, index));
        index++;
      } else {
        clearInterval(typingInterval);
      }
    }, 20);

    return () => clearInterval(typingInterval);
  }, [showContent, slide.description]);

  // Animal evolution animation
  useEffect(() => {
    if (!showContent || !slide.animals) return;

    const animals = slide.animals;
    let index = 0;

    const animalInterval = setInterval(() => {
      if (index < animals.length) {
        setAnimatedAnimals(prev => [...prev, animals[index]]);
        index++;
      } else {
        clearInterval(animalInterval);
      }
    }, 400);

    return () => clearInterval(animalInterval);
  }, [showContent, slide.animals]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 400);
  }, [isTransitioning, currentSlide]);

  const nextSlide = useCallback(() => {
    if (currentSlide < SLIDES.length - 1) {
      goToSlide(currentSlide + 1);
    }
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  }, [currentSlide, goToSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger navigation when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Generate floating particles
  const renderParticles = () => {
    if (!showParticles) return null;

    const particles = particleData[currentSlide];
    
    return particles.map((p, index) => (
      <div
        key={`${currentSlide}-${index}`}
        className="story-particle"
        style={{
          '--delay': `${p.delay}s`,
          '--x-start': `${p.xStart}%`,
          '--duration': `${p.duration}s`,
        }}
      >
        {p.particle}
      </div>
    ));
  };

  return (
    <div className={`onboarding-story bg-${slide.background}`}>
      {/* Background effects */}
      <div className="story-bg-gradient" />
      <div className="story-bg-stars" />
      <div className="story-bg-glow" />
      
      {/* Floating particles */}
      <div className="story-particles">
        {renderParticles()}
      </div>

      {/* Main content */}
      <div className={`story-content ${isTransitioning ? 'transitioning' : ''} ${showContent ? 'visible' : ''}`}>
        {/* Main emoji with effects */}
        <div className="story-emoji-container">
          <div className="story-emoji-glow" />
          <div className="story-emoji-ring" />
          <div className="story-emoji-ring ring-2" />
          <div className={`story-emoji ${slide.id === 'dragon' ? 'dragon-mode' : ''}`}>
            {slide.totemKey ? (
              <BadgeTotemIcon
                totemKey={slide.totemKey}
                label={`${BADGE_TOTEM_STAGES.find(stage => stage.key === slide.totemKey)?.label || 'Badge'} totem`}
                size={112}
              />
            ) : slide.emoji}
          </div>
          {slide.id === 'dragon' && (
            <div className="dragon-fire">
              <span>🔥</span>
              <span>🔥</span>
              <span>🔥</span>
            </div>
          )}
        </div>

        {/* Title with shimmer effect */}
        <h1 className="story-title">
          <span className="story-title-text">{slide.title}</span>
          <span className="story-title-shimmer" />
        </h1>

        {/* Subtitle */}
        <h2 className="story-subtitle">{slide.subtitle}</h2>

        {/* Evolution animals display */}
        {slide.animals && (
          <div className="evolution-showcase">
            {animatedAnimals.map((animal, index) => (
              <React.Fragment key={index}>
                <span 
                  className="evolution-animal"
                  style={{ '--delay': `${index * 0.4}s` }}
                >
                  <BadgeTotemIcon
                    totemKey={animal}
                    label={`${BADGE_TOTEM_STAGES.find(stage => stage.key === animal)?.label || 'Badge'} totem`}
                    size={40}
                    decorative
                  />
                </span>
              </React.Fragment>
            ))}
            {animatedAnimals.length < slide.animals.length && (
              <span className="evolution-dots">...</span>
            )}
          </div>
        )}

        {/* Description with typing effect */}
        <p className="story-description">
          {typedText}
          <span className="typing-cursor">|</span>
        </p>

        {/* Interactive health options */}
        {slide.inputType === 'health' && showContent && (
          <div className="onboarding-options">
            {HEALTH_OPTIONS.map(option => (
              <button
                key={option.id}
                className={`onboarding-option-btn ${selectedHealth.includes(option.id) ? 'selected' : ''}`}
                onClick={() => {
                  if (option.id === 'none') {
                    setSelectedHealth(['none']);
                  } else {
                    setSelectedHealth(prev => {
                      const filtered = prev.filter(h => h !== 'none');
                      return filtered.includes(option.id)
                        ? filtered.filter(h => h !== option.id)
                        : [...filtered, option.id];
                    });
                  }
                }}
              >
                <span className="option-emoji">{option.emoji}</span>
                <span className="option-label">{option.label}</span>
              </button>
            ))}
            {selectedHealth.includes('other') && (
              <input
                type="text"
                className="onboarding-other-input"
                placeholder="Please describe..."
                value={otherHealthText}
                onChange={(e) => setOtherHealthText(e.target.value)}
              />
            )}
          </div>
        )}

        {/* AI Coach avatar selection (Page 1) */}
        {slide.inputType === 'coach-avatar' && showContent && (
          <div className="onboarding-coach-section">
            <div className="coach-emoji-picker">
              <div className="coach-emoji-grid">
                {AI_EMOJI_OPTIONS.map(emoji => (
                  <button
                    key={emoji}
                    className={`coach-emoji-btn ${aiCoachEmoji === emoji ? 'selected' : ''}`}
                    onClick={() => {
                      setAiCoachEmoji(emoji);
                      analytics.onboarding.coachAvatarSelected(emoji);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="coach-preview">
              <span className="coach-preview-emoji">{aiCoachEmoji}</span>
              <span className="coach-preview-text">This is your assistant!</span>
            </div>
          </div>
        )}

        {/* AI Assistant style selection (Page 2) */}
        {slide.inputType === 'coach-style' && showContent && (
          <div className="onboarding-coach-section">
            <div className="coach-tone-picker">
              <div className="coach-section-label">Tone</div>
              <div className="coach-tone-grid">
                {AI_TONE_PRESETS.map(tone => (
                  <button
                    key={tone.id}
                    className={`coach-tone-btn ${aiCoachTone === tone.id ? 'selected' : ''}`}
                    onClick={() => {
                      setAiCoachTone(tone.id);
                      analytics.onboarding.coachStyleSelected(tone.id, tone.label);
                    }}
                  >
                    <span className="tone-emoji">{tone.emoji}</span>
                    <span className="tone-label">{tone.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="coach-intensity-picker">
              <div className="coach-section-label">Intensity</div>
              <div className="coach-intensity-slider">
                {['gentle', 'balanced', 'firm', 'intense'].map((level, idx) => (
                  <button
                    key={level}
                    className={`coach-intensity-btn ${aiCoachIntensity === level ? 'selected' : ''}`}
                    onClick={() => setAiCoachIntensity(level)}
                  >
                    <span className="intensity-bar">{'▮'.repeat(idx + 1)}</span>
                    <span className="intensity-label">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="coach-preview">
              <span className="coach-preview-emoji">{aiCoachEmoji}</span>
              <span className="coach-preview-text">
                {aiCoachTone === 'drill_sergeant' ? "No excuses! Let's crush it!" :
                 aiCoachTone === 'scientific' ? "Based on your data, optimal progress awaits." :
                 aiCoachTone === 'casual' ? "Hey! Ready to get after it?" :
                 aiCoachTone === 'zen' ? "Breathe. Each step forward matters." :
                 "You've got this! Every rep counts!"}
              </span>
            </div>
          </div>
        )}

        {/* Interactive diet options */}
        {slide.inputType === 'diet' && showContent && (
          <div className="onboarding-options">
            {DIET_OPTIONS.map(option => (
              <button
                key={option.id}
                className={`onboarding-option-btn ${selectedDiet.includes(option.id) ? 'selected' : ''}`}
                onClick={() => {
                  if (option.id === 'none') {
                    setSelectedDiet(['none']);
                    setAllergyText('');
                    setOtherDietText('');
                  } else {
                    setSelectedDiet(prev => {
                      const filtered = prev.filter(d => d !== 'none');
                      return filtered.includes(option.id)
                        ? filtered.filter(d => d !== option.id)
                        : [...filtered, option.id];
                    });
                  }
                }}
              >
                <span className="option-emoji">{option.emoji}</span>
                <span className="option-label">{option.label}</span>
              </button>
            ))}
          </div>
        )}
        {slide.inputType === 'diet' && showContent && (selectedDiet.includes('allergies') || selectedDiet.includes('other')) && (
          <div className="onboarding-text-inputs">
            {selectedDiet.includes('allergies') && (
              <input
                type="text"
                className="onboarding-other-input"
                placeholder="Which allergies? (e.g., nuts, shellfish)"
                value={allergyText}
                onChange={(e) => setAllergyText(e.target.value)}
              />
            )}
            {selectedDiet.includes('other') && (
              <input
                type="text"
                className="onboarding-other-input"
                placeholder="Other dietary preferences or restrictions..."
                value={otherDietText}
                onChange={(e) => setOtherDietText(e.target.value)}
              />
            )}
          </div>
        )}

        {/* Action buttons for final slide */}
        {slide.isFinal && showContent && (
          <div className="story-actions">
            <button 
              className="story-btn story-btn-primary"
              onClick={openPlanWizard}
            >
              <span className="story-btn-icon">📋</span>
              <span className="btn-text">Create My Plan</span>
              <span className="story-btn-shimmer" />
            </button>
            
            <div className="story-actions-divider">
              <span>or start with</span>
            </div>
            
            <button 
              className="story-btn story-btn-secondary"
              onClick={async () => {
                await saveProfileData();
                onComplete?.();
                openNewActivityModal?.({ initialOption: 'newWorkout' });
              }}
            >
              <span className="story-btn-icon">⚡</span>
              <span className="btn-text">Quick Activity</span>
            </button>
            
            <button 
              className="story-btn-skip"
              onClick={async () => {
                await saveProfileData();
                onComplete?.();
              }}
            >
              Skip intro →
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="story-navigation">
        {/* Progress dots */}
        <div className="story-dots">
          {SLIDES.map((s, index) => (
            <button
              key={s.id}
              className={`story-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Arrow navigation */}
        <div className="story-arrows">
          <button 
            className={`story-arrow story-arrow-prev ${currentSlide === 0 ? 'disabled' : ''}`}
            onClick={prevSlide}
            disabled={currentSlide === 0}
          >
            ←
          </button>
          
          {!slide.isFinal && (
            <button 
              className="story-arrow story-arrow-next"
              onClick={nextSlide}
            >
              <span>Continue</span>
              <span className="arrow-icon">→</span>
            </button>
          )}
        </div>
      </div>

      {/* Skip button (not on final slide) */}
      {!slide.isFinal && (
        <button 
          className="story-skip"
          onClick={() => goToSlide(SLIDES.length - 1)}
        >
          Skip to actions →
        </button>
      )}
    </div>
  );
};

export default OnboardingStory;
